import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useOutsideDetect } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import styled from '@mint-lib/styled';
import { uniq } from 'remeda';
import { z } from 'zod';

import {
  Box,
  Button,
  Input,
  LoadingButton,
  Popover,
  Select,
  ToggleButton,
  Typography,
} from '@myn/mui';

import { usePreparedBuilderSchema } from '../../../../hooks/useGetBuilderSchemaData.js';
import {
  CustomColumnFormat,
  CustomColumnType,
} from '../../../../types/builderTableView.js';
import {
  getFormatOptions,
  getTypeOptions,
} from '../../../../utils/campaignsBuilder.js';
import { CustomColumn } from './CustomColumnForm.types.js';
import OldOptionField from './OptionField/_OldOptionField.jsx';
import OptionField from './OptionField/OptionField.jsx';
import PreEditModal from './PreEditModal.jsx';

const CREATE_REQUIRED_FIELDS = ['name', 'format', 'type'];

const EDIT_REQUIRED_FIELDS = ['name'];

const defaultValues: CustomColumn = {
  name: '',
  format: null,
  type: null,
  options: [],
};

const getSchema = (
  items: ReturnType<typeof usePreparedBuilderSchema>['customColumns'],
  isEditMode: boolean,
) =>
  z.object({
    name: z.string().superRefine((value, ctx) => {
      const isUnique = !items.map((item) => item.name).includes(String(value));
      if (!isUnique && !isEditMode) {
        ctx.addIssue({
          code: 'custom',
          message: `${value} isn't unique name.`,
        });
      }
    }),
    format: z
      .union([
        z.literal(CustomColumnFormat.text),
        z.literal(CustomColumnFormat.number),
        z.literal(CustomColumnFormat.currency),
        z.literal(CustomColumnFormat.percentage),
        z.literal(CustomColumnFormat.integer),
      ])
      .nullable(),
    type: z
      .union([
        z.literal(CustomColumnType.singleInput),
        z.literal(CustomColumnType.select),
      ])
      .nullable(),
    options: z
      .array(
        z.object({
          name: z.string(),
          value: z.string(),
          isDisabled: z.boolean(),
        }),
      )
      .optional(),
  });

export type Props = {
  isOpen: boolean;
  isEditMode?: boolean;
  editableValues: CustomColumn | null;
  onSubmit: (
    data: CustomColumn,
    onReset: () => void,
    onFailed: () => void,
  ) => void;
  onEdit: (
    data: CustomColumn,
    onReset: () => void,
    onFailed: () => void,
  ) => void;
  onDelete: (onReset: () => void, onFailed: () => void) => void;
  onCustomSaveChange: (isActive: boolean) => void;
  onCancel: () => void;
  className?: string;
  anchorEl: HTMLElement | null;
  preEditMode?: boolean;
  setPreEditMode: (value: boolean) => void;
};

const CustomColumnForm: React.FC<Props> = ({
  isOpen = false,
  isEditMode = false,
  editableValues,
  onSubmit,
  onEdit,
  onDelete,
  onCancel,
  onCustomSaveChange,
  className,
  anchorEl,
  preEditMode,
  setPreEditMode,
}) => {
  const { t } = useTranslation('@myn/planning');
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);

  const [isWarningOpen, setIsWarningOpen] = useState<boolean>(false);
  const [isSuccessfullyRemoved, setIsSuccessfullyRemoved] =
    useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

  const { customColumns } = usePreparedBuilderSchema();
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const formatOptions = useMemo(() => getFormatOptions(t), [t]);
  const typeOptions = useMemo(() => getTypeOptions(t), [t]);

  const REQUIRED_FIELDS = isEditMode
    ? EDIT_REQUIRED_FIELDS
    : CREATE_REQUIRED_FIELDS;

  const {
    handleSubmit: submit,
    control,
    setValue,
    setError,
    reset,
    clearErrors,
    formState,
  } = useForm<CustomColumn>({
    defaultValues,
    resolver: zodResolver(getSchema(customColumns, isEditMode)),
  });

  const iRequiresFieldsEmpty = useMemo(
    () =>
      !REQUIRED_FIELDS.every(
        (key) =>
          formState.dirtyFields[key as keyof typeof formState.dirtyFields],
      ),
    [formState],
  );

  const isSaveDisabled = isEditMode
    ? !formState.isDirty || Boolean(Object.keys(formState.errors).length)
    : !formState.isDirty ||
      Boolean(Object.keys(formState.errors).length) ||
      iRequiresFieldsEmpty;

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'options',
  });
  const values = useWatch({ control });

  useEffect(() => {
    if (
      values.type === CustomColumnType.select &&
      !values.options?.length &&
      !isEditMode
    ) {
      setValue('options', [{ name: '', value: '', isDisabled: false }]);
    }

    if (
      values.type === CustomColumnType.singleInput &&
      values.options?.length
    ) {
      setValue('options', []);
    }
  }, [values, isEditMode]);

  const handleOptionAddition = useCallback(() => {
    append({ value: '', name: '', isDisabled: false });
  }, [append]);

  const handleWarningToggle = useCallback(() => {
    setIsWarningOpen((state) => !state);
  }, []);

  const handleSubmit = useCallback(
    (data: CustomColumn) => {
      const optionNames = data.options?.map(({ name }) => name) ?? [];
      const isUnique = optionNames?.length === uniq(optionNames).length;

      const onReset = () => {
        reset(defaultValues);
        setValue('options', []);
        setIsSubmitting(false);
        setPreEditMode(false);
      };

      const onFailed = () => {
        setIsSubmitting(false);
      };

      if (!isEditMode && isUnique) {
        setIsSubmitting(true);
        onSubmit(data, onReset, onFailed);
      }

      if (isEditMode && isUnique) {
        setIsSubmitting(true);
        onEdit(data, onReset, onFailed);
      }
    },
    [onSubmit, onEdit, isEditMode, reset],
  );

  const handleDelete = useCallback(() => {
    const onReset = () => {
      reset(defaultValues);
      setValue('options', []);
      setIsSuccessfullyRemoved(true);
      setIsSubmitting(false);
      setIsWarningOpen(false);
    };

    const onFailed = () => {
      setIsSubmitting(false);
    };

    setIsSubmitting(true);

    onDelete(onReset, onFailed);
  }, [onDelete]);

  const handleCancel = useCallback(() => {
    reset(defaultValues);
    onCancel();
  }, [reset, onCancel]);

  useEffect(() => {
    if (isEditMode && editableValues) {
      reset(editableValues);
    }

    if (!isEditMode) {
      reset(defaultValues);
    }
  }, [reset, isEditMode, editableValues]);

  useEffect(() => {
    if (!isOpen) {
      setIsSuccessfullyRemoved(false);
    }
  }, [isOpen]);

  useEffect(() => {
    onCustomSaveChange(!isSaveDisabled);
  }, [isSaveDisabled]);

  useOutsideDetect(popoverRef, isOpen, () => {
    if (!isSelectModalOpen) {
      setIsWarningOpen(false);
      handleCancel();
    }
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
    >
      <Box ref={popoverRef}>
        {preEditMode ? (
          <PreEditModal
            isWarningOpen={isWarningOpen}
            handleWarningToggle={handleWarningToggle}
            setPreEditMode={setPreEditMode}
            handleDelete={handleDelete}
            handleCancel={handleCancel}
          />
        ) : (
          <Form
            onSubmit={submit(handleSubmit)}
            className={className}
            $isHidden={isWarningOpen || isSuccessfullyRemoved}
            aria-label="from"
          >
            <RowWrapper>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <Input
                    type="text"
                    label={t('Custom column name')}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('Column name')}
                    error={Boolean(fieldState.error)}
                    disabled={isSubmitting}
                    fullWidth
                  />
                )}
              />
            </RowWrapper>
            <RowWrapper>
              <Controller
                control={control}
                name="format"
                render={({ field, fieldState }) => (
                  <Select
                    label={t('Format')}
                    getOpenState={(open) => setIsSelectModalOpen(open)}
                    options={formatOptions}
                    error={Boolean(fieldState.error)}
                    disabled={isEditMode || isSubmitting}
                    value={field.value || ''}
                    onChange={field.onChange}
                    fullWidth
                    placeholder={t('Select format')}
                  />
                )}
              />
            </RowWrapper>
            <RowWrapper>
              <Controller
                control={control}
                name="type"
                render={({ field, fieldState }) => (
                  <Select
                    value={field.value || ''}
                    getOpenState={(open) => setIsSelectModalOpen(open)}
                    onChange={field.onChange}
                    disabled={isEditMode || isSubmitting}
                    options={typeOptions}
                    error={Boolean(fieldState.error)}
                    placeholder={t('Select type')}
                    label={t('Type')}
                    fullWidth
                  />
                )}
              />
            </RowWrapper>
            {values.type === CustomColumnType.select && values.format ? (
              <Box>
                <Typography
                  variant="bodyShort01"
                  sx={{ display: 'inline-block', m: '16px 0' }}
                >
                  {t(
                    'List of fields allows you to select a single value from predefined set of options:',
                  )}
                </Typography>
                {fields.map((field, index) =>
                  ff.mdmColumnsForBuyingPlan.enabled ? (
                    <StyledOptionField
                      key={field.id}
                      control={control}
                      index={index}
                      setError={setError}
                      isSubmitted={formState.isSubmitted}
                      clearErrors={clearErrors}
                      onRemove={remove}
                      onMove={move}
                    />
                  ) : (
                    <StyledOptionFieldOld
                      key={field.id}
                      control={control}
                      index={index}
                      setError={setError}
                      isSubmitted={formState.isSubmitted}
                      clearErrors={clearErrors}
                      onRemove={remove}
                      onMove={move}
                    />
                  ),
                )}
                <ToggleButton
                  value="Add a value"
                  fullWidth
                  size="medium"
                  disabled={isSubmitting}
                  onClick={handleOptionAddition}
                  label={t('Add value')}
                  endIcon="Add"
                />
              </Box>
            ) : null}
            <FooterWrapper>
              <Button variant="text" onClick={handleCancel}>
                {t('Cancel')}
              </Button>

              <LoadingButton
                type="submit"
                variant="contained"
                disabled={isSaveDisabled}
                loading={isSubmitting}
              >
                {t('Save')}
              </LoadingButton>
            </FooterWrapper>
          </Form>
        )}
      </Box>
    </Popover>
  );
};

const Form = styled('form')<{ $isHidden?: boolean }>`
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing(1)};
  flex-direction: column;
  position: relative;
  width: 320px;
`;

const RowWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const StyledOptionFieldOld = styled(OldOptionField)`
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const StyledOptionField = styled(OptionField)`
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const FooterWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

export default CustomColumnForm;
